import { messengerStore } from '@dinosband/bv-messenger-controller'
import { Message } from '@dinosband/bv-messenger-schema'
import { _findLast, _last } from '@dinosband/dbi-utils'
import { cachedMessage, CachedMessage } from './util'

interface CidInfo {
  id: number
  seq: number
  cid: number
}

const MessageCidMap: Record<number, {min: CidInfo, max: CidInfo}> = {}

export async function updateFilteredMessageCache (chatRoomId: number, messages: Message[]) {
  const cache = (await import('@dinosband/bv-cache'))?.messageCache
  const firstSeq = messages[0]?.seq
  const messages_ = messages as unknown as CachedMessage[]
  const contents = await cache.contents(chatRoomId) as unknown as CachedMessage[]
  if (firstSeq != undefined) {
    let firstCid = 1
    const found = cachedMessage(contents, firstSeq)
    if (found?._cid != undefined) {
      firstCid = found._cid
    }
    messages_.forEach((i, idx) => i._cid = idx + firstCid)
  }

  const valids: number[] = []
  const outdateds: CachedMessage[] = []
  contents.forEach((message0) => {
    const seq = message0.seq
    const message1 = cachedMessage(messages_, seq)
    if (message1) {
      if (message0._cid === message1._cid) {
        valids.push(seq)
      }
    } else {
      if (message0._cid != undefined) {
        message0._cid = undefined
        outdateds.push(message0)
      }
    }
  })

  const selecteds = [
    ...outdateds,
    ...messages_.filter((i) => !valids.includes(i.seq)),
  ]

  if (selecteds.length) {
    //   'contents:', contents.map(_logItems),
    //   'messages:', messages.map(_logItems),
    //   'changeds:', selecteds.map(_logItems),
    // )
    await cache.bulkPut(chatRoomId, selecteds)
  } else {
    //   'unchanged', messages.map(_logItems))
  }

  if (messages_.length) {
    const first = await trimCache(chatRoomId, messages)
    const last = _last(messages_)
    _updateCidMap(chatRoomId, first, last)
  } else {
    _updateCidMap(chatRoomId)
  }
}

export async function cachePut (message: Message, withCid = false) {
  const cache = (await import('@dinosband/bv-cache'))?.messageCache
  if (withCid) {
    _updateCid(message)
  } else {
    if (_isFilteredChatRoom(message.chatRoomId)) {
      await cacheEdit(message)
      return
    }
  }
  await cache.put(message.chatRoomId, message)
}

export async function cacheBulkPut (chatRoomId: number | undefined, messages: Message[]) {
  const cache = (await import('@dinosband/bv-cache'))?.messageCache
  if (!chatRoomId) {
    await Promise.all(messages.map((i) => cachePut(i)))
    return
  }
  if (_isFilteredChatRoom(chatRoomId)) {
    const founds = cache.readBulk(chatRoomId, messages.map((i)=> i.seq)) as unknown as CachedMessage[]
    messages.forEach((i: CachedMessage) => {
      const found = cachedMessage(founds, i.seq)
      i._cid = found?._cid
    })
  }
  await cache.bulkPut(chatRoomId, messages)
}

export async function cacheEdit (message: Message, withCid = false) {
  const cache = (await import('@dinosband/bv-cache'))?.messageCache
  const chatRoomId = message.chatRoomId
  const found = cache.read(message.chatRoomId, message.seq)
  if (!found) {
    return
  }
  if (withCid) {
    (message as CachedMessage)._cid = (found as CachedMessage)._cid
  }
  await cache.put(chatRoomId, message)
}

export async function registerCid (chatRoomId: number) {
  const cache = (await import('@dinosband/bv-cache'))?.messageCache
  const contents = (await cache.contents(chatRoomId) ?? []) as unknown as CachedMessage[]
  const first = contents.find((i) => i._cid != undefined)
  const last = _findLast(contents, (i) => i._cid != undefined)
  _updateCidMap(chatRoomId, first, last)
}

function _updateCid (message: Message) {
  const chatRoomId = message.chatRoomId
  const { min, max } = MessageCidMap[chatRoomId] ?? {}
  if (!min || !max) {
    const info = _setCid(message, 0)
    MessageCidMap[chatRoomId] = { min: info, max: info }
    return true
  }
  if (message.id < min.id) {
    const info = _setCid(message, min.cid - 1)
    MessageCidMap[chatRoomId] = { min: info, max }
    return true
  }
  if (max.id < message.id) {
    const info = _setCid(message, max.cid + 1)
    MessageCidMap[chatRoomId] = { min, max: info }
    return true
  }
  return false
}

function _setCid (message: Message, cid: number) {
  const cm = message as unknown as CachedMessage
  cm._cid = cid
  return { id: message.id, seq: message.seq, cid }
}

function _updateCidMap (chatRoomId: number, first?: CachedMessage, last?: CachedMessage) {
  if (!first || !last || first._cid == undefined || last._cid == undefined) {
    delete MessageCidMap[chatRoomId]
    return
  }
  const min = { id: first.id, seq: first.seq, cid: first._cid }
  const max = { id: last.id, seq: last.seq, cid: last._cid }
  MessageCidMap[chatRoomId] = { min, max }
}

async function trimCache (chatRoomId: number, messages: Message[]) {
  const cache = (await import('@dinosband/bv-cache'))?.messageCache
  let ref = messages[0]
  if (!ref) {
    return
  }
  const overCount = messages.length - cache.maxCount
  if (0 < overCount) {
    ref = messages[overCount]
    if (!ref) {
      // console.error('trimCache', chatRoomId, 'not found cid messages', messages, overCount)
      return
    }
  }
  if (!await cache.isReservedToDelete(chatRoomId, ref.seq)) {
    return ref
  }
  await cache.trim(chatRoomId, (i, excessCount) => {
    if (excessCount <= 0) {
      return { del: false, stop: true }
    }
    if (i.seq < ref.seq) {
      return { del: true }
    }
    const del = (i as unknown as CachedMessage)._cid == undefined
    return { del }
  })
  return ref
}

// function _logItems (message: Message) {
//   return [message.seq, (message as unknown as CachedMessage)._cid]
// }

function _isFilteredChatRoom (chatRoomId: number) {
  return messengerStore.chatRoomContext(chatRoomId).isFiltered
}